import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'delivery-integration',
  title: 'Delivery-Integration',
  shortTitle: 'Delivery',
  icon: 'Bike',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1593929976216-f746e488aa45?w=1200&h=900&fit=crop',
  teaser: 'Alle Lieferbestellungen in einem System: Wie du Lieferplattformen an Kasse und Küche anbindest, Provisionen im Griff behältst und einen eigenen Bestellkanal aufbaust.',
  mainArticle: {
    title: 'Delivery-Integration: Lieferplattformen und Kasse verbinden',
    takeaways: [
      'Integration bündelt alle Lieferplattformen in Kasse und Küche – kein Abtippen, eine Speisekarte.',
      'Plattformen für Neukunden, eigener Bestellkanal für Stammkunden – so bleibt Marge im Haus.',
      'Lieferkarte reduzieren, Preise kanalspezifisch kalkulieren, Verpackung einrechnen.',
      'Kapazität aktiv steuern, damit Lieferwellen den Restaurantservice nicht ruinieren.',
    ],
    intro: 'Drei Tablets an der Wand, jedes piept anders, und jemand tippt jede Bestellung von Hand in die Kasse – so sieht Delivery in vielen Küchen aus. Delivery-Integration bündelt alle Lieferkanäle in einem System. Dieser Leitfaden erklärt, wie das funktioniert, was es kostet und wie du das Liefergeschäft wirtschaftlich gestaltest.',
    sections: [
      section(
        'Ausgangslage: Das Tablet-Chaos',
        p('Jede Lieferplattform liefert ihr eigenes Gerät. Bestellungen kommen parallel herein, müssen einzeln bestätigt, in die Kasse übertragen und an die Küche weitergegeben werden. In Stoßzeiten entstehen dabei Tippfehler, vergessene Bestellungen, falsche Abholzeiten – und am Monatsende passen Plattformabrechnung und Kassenumsatz nicht zusammen.'),
        p('Dazu kommt die Speisekartenpflege: Jede Preisänderung, jedes neue Gericht und jeder ausverkaufte Artikel müssen auf jeder Plattform einzeln aktualisiert werden. Wer drei Plattformen und einen eigenen Webshop betreibt, pflegt vier Speisekarten.')
      ),
      section(
        'Was Delivery-Integration bedeutet',
        p('Bei der Integration werden alle Lieferkanäle über eine Schnittstelle mit deiner Kasse verbunden – entweder direkt oder über eine Middleware, die zwischen Plattformen und Kasse vermittelt. Das Ergebnis:'),
        list([
          '**Alle Bestellungen landen automatisch in der Kasse** – ohne Abtippen, mit korrekter Zuordnung von Artikeln, Preisen und Zahlungsart',
          '**Automatische Annahme** mit konfigurierbaren Zubereitungszeiten je Tageszeit und Auslastung',
          '**Weiterleitung an die Küche** per Drucker oder Küchendisplay, gekennzeichnet nach Plattform und Abholzeit',
          '**Zentrale Speisekartenpflege:** Ein Artikelstamm, Änderungen werden an alle Plattformen übertragen',
          '**Verfügbarkeit steuern:** Ausverkauft-Markierung und Pausieren einzelner Plattformen mit einem Klick',
          '**Einheitliches Reporting:** Umsatz je Kanal, Provisionen, Stornos, Zubereitungszeiten',
        ])
      ),
      section(
        'Die Vorteile im Alltag',
        table(
          ['Vorher', 'Mit Integration'],
          [
            ['3 – 4 Tablets, manuelle Annahme', 'Ein System, automatische Annahme'],
            ['Jede Bestellung von Hand in die Kasse', 'Bestellungen automatisch verbucht'],
            ['Speisekarte auf jeder Plattform einzeln pflegen', 'Zentrale Pflege, automatische Synchronisation'],
            ['Küche erhält Bons aus verschiedenen Quellen in verschiedenen Formaten', 'Einheitliche Küchenbons mit Kanal und Uhrzeit'],
            ['Umsatzabgleich am Monatsende per Hand', 'Kanalumsätze im Kassenreport'],
          ]
        ),
        p('Der Zeitgewinn liegt bei Betrieben mit 50 Lieferbestellungen am Tag leicht bei **zwei bis drei Stunden täglich** – Zeit, die bisher am Tablet und an der Kasse verloren ging.')
      ),
      section(
        'Plattformen oder eigener Bestellkanal?',
        p('Lieferplattformen bringen Reichweite und Neukunden, kosten aber Provisionen von häufig 13 bis 30 Prozent des Bestellwerts. Ein eigener Bestellkanal – Webshop oder Bestellseite mit Abholung und Lieferung – ist provisionsfrei, muss aber selbst beworben werden. Die meisten erfolgreichen Betriebe fahren beides: Plattformen zur Neukundengewinnung, den eigenen Kanal für Stammkunden.'),
        tip('Lege jeder Plattformbestellung einen Flyer mit QR-Code zum eigenen Bestellkanal bei – gern mit kleinem Vorteil für die nächste Bestellung. So wandern Neukunden von der Plattform in deinen provisionsfreien Kanal.')
      ),
      section(
        'Speisekarte für die Lieferung anders denken',
        p('Nicht jedes Gericht eignet sich für 20 Minuten Transport. Eine Lieferkarte sollte bewusst reduziert und angepasst sein:'),
        list([
          'Gerichte, die Transport und Verpackung gut überstehen (keine Pommes unter Deckel, keine Saucen auf dem Salat)',
          'Klar strukturierte Varianten und Extras – kurz und eindeutig',
          'Verpackungsfähige Portionsgrößen und Menü-Bundles',
          'Eigene Preise für Lieferkanäle, um Provisionen teilweise abzufedern (rechtlich zulässig, transparent gestalten)',
          'Fotos für jedes Gericht – auf Plattformen entscheidet das Bild',
        ])
      ),
      section(
        'Küchenprozesse und Kapazitätssteuerung',
        p('Lieferung und Restaurantbetrieb konkurrieren um dieselbe Küche. Ohne Steuerung kommt am Freitagabend die Lieferwelle genau dann, wenn das Restaurant voll ist. Gute Integrationen bieten dafür Werkzeuge:'),
        list([
          '**Dynamische Zubereitungszeiten:** Bei hoher Auslastung werden längere Zeiten an die Plattformen gemeldet',
          '**Drosselung:** Maximale Anzahl Lieferbestellungen pro 15 Minuten',
          '**Pausieren:** Einzelne Plattformen kurzfristig abschalten, ohne das Tablet zu suchen',
          '**Packstation:** Ein fester Bereich mit Verpackung, Etiketten und Abholregal für Fahrer',
        ])
      ),
      section(
        'Wirtschaftlichkeit: Rechnet sich Delivery überhaupt?',
        p('Delivery ist ein eigener Geschäftszweig mit eigener Kalkulation. Ein Gericht für 15 € bringt nach Provision (z. B. 30 % = 4,50 €), Wareneinsatz (30 % = 4,50 €) und Verpackung (0,80 €) noch rund 5,20 € Deckungsbeitrag – bevor Personal und Fixkosten bezahlt sind. Das kann sich lohnen, wenn Küche und Personal ohnehin da sind und die Zusatzbestellungen freie Kapazität nutzen. Es wird zum Verlustgeschäft, wenn dafür zusätzliches Personal eingeplant wird oder das Restaurantgeschäft leidet.'),
        p('Die genaue Kalkulation mit Beispielen findest du im Vertiefungsartikel zur Delivery-Kalkulation.')
      ),
      section(
        'Auswahlkriterien für eine Integration',
        steps([
          '**Kassenkompatibilität:** Gibt es eine echte Schnittstelle zu deiner Kasse oder nur einen separaten Drucker?',
          '**Plattform-Abdeckung:** Werden alle Plattformen unterstützt, die du nutzt oder nutzen willst?',
          '**Speisekarten-Sync:** Können Artikel, Preise und Verfügbarkeiten zentral gepflegt und automatisch übertragen werden?',
          '**Kapazitätssteuerung:** Dynamische Zeiten, Drosselung, Pausieren?',
          '**Eigener Bestellkanal:** Ist ein Webshop enthalten oder anbindbar?',
          '**Kostenmodell:** Feste Gebühr pro Monat oder Prozent je Bestellung – auf dein Volumen hochrechnen.',
          '**Reporting:** Werden Provisionen und Auszahlungen je Plattform nachvollziehbar ausgewiesen?',
        ])
      ),
      section(
        'Die häufigsten Fehler im Liefergeschäft',
        list([
          '**Vollständige Karte auf allen Plattformen:** Zu viele Artikel, zu viele Varianten, schlechte Transportqualität.',
          '**Gleiche Preise wie im Haus:** Provisionen werden nicht eingerechnet, das Geschäft wird zum Zuschuss.',
          '**Keine Kapazitätssteuerung:** Lieferwellen ruinieren den Restaurantservice.',
          '**Verpackung nicht kalkuliert:** 0,60 – 1,50 € pro Bestellung sind schnell vergessen.',
          '**Plattform-Reporting ungeprüft:** Provisionen, Werbekosten und Stornos werden nicht abgeglichen.',
        ])
      ),
      section(
        'Oft gefragt',
        faq([
          { q: 'Brauche ich eine Integration, wenn ich nur eine Plattform nutze?', a: 'Auch bei einer Plattform spart die Kassenanbindung das Abtippen und sorgt für korrekte Umsätze im Tagesabschluss. Der Nutzen steigt aber mit jeder weiteren Plattform deutlich.' },
          { q: 'Können Plattformbestellungen automatisch angenommen werden?', a: 'Ja, das ist einer der Hauptvorteile. Du legst Zubereitungszeiten und Grenzen fest, das System bestätigt innerhalb dieser Regeln automatisch. Bei Überlastung wird die Zeit verlängert oder die Annahme pausiert.' },
          { q: 'Wie gehe ich mit Stornos und Reklamationen um?', a: 'Kläre pro Plattform, wie Stornos gemeldet und abgerechnet werden. Eine gute Integration kennzeichnet Stornos in der Kasse und im Reporting, damit du am Monatsende Abweichungen zur Plattformabrechnung erkennst.' },
          { q: 'Lohnt sich ein eigener Fahrerdienst?', a: 'Das hängt vom Bestellvolumen und Liefergebiet ab. Eigene Fahrer kosten Fixkosten, sparen aber Provisionen. Viele Betriebe nutzen die Plattform-Lieferung für Reichweite und bieten den eigenen Kanal zunächst nur mit Abholung an.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'eigener-webshop-vs-plattformen',
      title: 'Eigener Bestellkanal vs. Lieferplattformen: Provisionen, Reichweite, Kontrolle',
      shortTitle: 'Eigener Shop vs. Plattform',
      teaser: 'Wann sich ein eigener Webshop lohnt, wie du Gäste von der Plattform in den eigenen Kanal holst und welche Kennzahlen zeigen, ob die Strategie funktioniert.',
      intro: 'Plattformen bringen Kunden – und nehmen dafür bis zu einem Drittel des Umsatzes. Ein eigener Bestellkanal spart Provisionen, braucht aber Aufmerksamkeit. Die richtige Mischung entscheidet über die Marge im Liefergeschäft.',
      sections: [
        section(
          'Reichweite kaufen oder Marge behalten?',
          table(
            ['', 'Lieferplattform', 'Eigener Bestellkanal'],
            [
              ['Reichweite', 'Hoch – Gäste suchen dort aktiv', 'Nur eigene Gäste, muss beworben werden'],
              ['Kosten', 'Provision 13 – 30 % je Bestellung', 'Feste Gebühr, oft 30 – 150 € / Monat + Zahlungsgebühren'],
              ['Kundendaten', 'Bleiben bei der Plattform', 'Gehören dir – Basis für Newsletter und Loyalty'],
              ['Wettbewerb', 'Konkurrenz eine Zeile darunter', 'Keiner'],
              ['Lieferung', 'Plattformfahrer verfügbar', 'Eigene Fahrer oder nur Abholung'],
              ['Sinnvoll für', 'Neukunden, neue Standorte, Sichtbarkeit', 'Stammkunden, Wiederholungsbestellungen'],
            ]
          )
        ),
        section(
          'Die Kanalstrategie',
          p('Bewährt hat sich ein zweistufiges Modell: Plattformen als **Akquisekanal**, der eigene Shop als **Stammkundenkanal**. Ziel ist, dass ein Gast maximal ein- bis zweimal über die Plattform bestellt und danach direkt bei dir. Jede verlagerte Bestellung spart bei 30 € Bestellwert und 25 % Provision rund 7,50 € – bei 200 verlagerten Bestellungen im Monat 1.500 €.')
        ),
        section(
          'Anforderungen an den eigenen Bestellkanal',
          list([
            '**Kassenanbindung:** Bestellungen laufen wie Plattformbestellungen automatisch in Kasse und Küche',
            '**Mobil optimiert** und ohne Registrierungspflicht',
            '**Abhol- und Lieferzeiten** mit Kapazitätsgrenzen',
            '**Online-Zahlung** mit gängigen Verfahren, optional Barzahlung bei Abholung',
            '**Bestellbestätigung** per E-Mail oder SMS mit Abholzeit',
            '**Gutschein- und Rabattcodes** für die Umlenkung von der Plattform',
            '**Verknüpfung** mit Google-Unternehmensprofil, Website und Social Media',
          ])
        ),
        section(
          'Gäste in den eigenen Kanal holen',
          steps([
            'Flyer mit QR-Code zum eigenen Shop in jede Plattformbestellung legen – mit Vorteil für die nächste Direktbestellung',
            'Bestell-Button auf dem Google-Unternehmensprofil auf den eigenen Shop richten',
            'Eigene Preise: Im eigenen Kanal etwas günstiger als auf der Plattform',
            'Loyalty-Programm nur im eigenen Kanal anbieten (Stempel, Punkte)',
            'Regelmäßig in Social Media auf den Direktbestell-Link hinweisen',
          ]),
          tip('Prüfe die Nutzungsbedingungen der Plattformen: Manche untersagen Werbung für eigene Kanäle in der Plattformverpackung. Ein neutraler Flyer mit deinem Logo und QR-Code ist in der Regel unproblematisch.')
        ),
        section(
          'Kennzahlen für die Strategie',
          list([
            'Anteil Direktbestellungen am gesamten Lieferumsatz (Ziel: steigend, langfristig über 40 %)',
            'Durchschnittlicher Bestellwert je Kanal',
            'Wiederbestellrate im eigenen Kanal',
            'Effektive Provisionsquote über alle Kanäle',
            'Einlösungsrate der Flyer-Codes',
          ])
        ),
      ],
    },
    {
      slug: 'kuechenprozesse-lieferung-abholung',
      title: 'Küchenprozesse für Lieferung und Abholung optimieren',
      shortTitle: 'Küchenprozesse Delivery',
      teaser: 'Packstation, Kapazitätssteuerung, Fahrerübergabe: So organisierst du Lieferung und Abholung, ohne dass der Restaurantbetrieb darunter leidet.',
      intro: 'Die Küche ist dieselbe, die Anforderungen sind andere: Lieferbestellungen müssen pünktlich, vollständig und transportfähig verpackt sein – während im Restaurant der Service läuft. Mit klaren Prozessen gelingt beides.',
      sections: [
        section(
          'Kanäle trennen, Küche zusammenhalten',
          p('Lieferbestellungen sollten in der Küche sofort als solche erkennbar sein – durch farbige Bons, eigene Spalte im Küchendisplay oder feste Kennzeichnung. Sie brauchen andere Prioritäten: Ein Restaurantgast wartet am Tisch, ein Fahrer steht in fünf Minuten an der Tür. Definiere, wer bei Konflikten entscheidet, und richte die Reihenfolge nach Abholzeit statt nach Eingang aus.')
        ),
        section(
          'Die Packstation',
          list([
            'Fester Bereich nahe der Ausgabe mit allen Verpackungen, Besteck, Servietten, Saucenbechern',
            'Etikettendrucker oder Bons mit Bestellnummer, Kanal und Inhalt zum Aufkleben',
            'Checkliste pro Bestellung: Alle Positionen, Extras, Getränke, Beilagen?',
            'Warmhaltebereich oder -schrank für fertige Bestellungen bis zur Abholung',
            'Abholregal mit Nummern, damit Fahrer und Abholer ohne Nachfrage zugreifen können',
          ]),
          tip('Eine Person, die in Stoßzeiten ausschließlich packt und kontrolliert, reduziert Reklamationen wegen fehlender Positionen drastisch – die häufigste Beschwerde im Liefergeschäft.')
        ),
        section(
          'Kapazität steuern statt erleiden',
          p('Deine Küche hat eine Obergrenze. Wird sie überschritten, leiden alle Kanäle: Restaurantgäste warten, Lieferungen kommen kalt an, Bewertungen sinken. Steuere aktiv:'),
          list([
            'Zubereitungszeiten je Tageszeit an Plattformen melden – freitags 20 Uhr realistisch 45 statt 25 Minuten',
            'Maximale Bestellungen pro Zeitfenster festlegen',
            'Bei Überlastung einzelne Plattformen kurz pausieren',
            'Liefergebiet in Stoßzeiten verkleinern, um Fahrzeiten zu reduzieren',
            'Eigene Abholslots im Webshop begrenzen',
          ])
        ),
        section(
          'Übergabe an Fahrer und Abholer',
          p('Der Übergabepunkt sollte außerhalb des Servicebereichs liegen – Fahrer im Gastraum stören Gäste und Personal. Ein separater Eingang oder ein Fenster ist ideal. Bestellnummern gut sichtbar, kurze Wege, klare Zuständigkeit. Bei Verspätungen des Fahrers: fertige Bestellungen im Warmhaltebereich, nicht auf der Theke.')
        ),
        section(
          'Qualität sichern',
          list([
            'Verpackung testen: Gericht 20 Minuten stehen lassen und probieren',
            'Komponenten trennen (Sauce separat, Salat getrennt, Frittiertes mit Lüftung)',
            'Heiß und kalt in getrennten Behältern',
            'Reklamationen je Gericht auswerten – Gerichte mit schlechter Transportqualität aus der Lieferkarte nehmen',
            'Stichproben durch die Küchenleitung: eine Bestellung pro Schicht öffnen und prüfen',
          ])
        ),
      ],
    },
    {
      slug: 'delivery-kalkulation',
      title: 'Delivery-Kalkulation: Provisionen, Verpackung und Preisgestaltung',
      shortTitle: 'Delivery-Kalkulation',
      teaser: 'Rechnet sich dein Liefergeschäft? Eine Schritt-für-Schritt-Kalkulation mit Beispielen – und Stellschrauben, um die Marge zu verbessern.',
      intro: 'Viele Betriebe wissen nicht, ob sie mit Lieferung Geld verdienen oder Umsatz gegen Marge tauschen. Eine ehrliche Kalkulation dauert eine Stunde und ist die wichtigste Entscheidungsgrundlage im Liefergeschäft.',
      sections: [
        section(
          'Die Kostenbestandteile einer Lieferbestellung',
          list([
            '**Wareneinsatz** des Gerichts (inkl. Beilagen und Extras)',
            '**Plattformprovision** in Prozent des Bestellwerts (häufig 13 – 30 %, je nach Modell mit oder ohne Lieferung)',
            '**Zahlungsgebühren**, sofern nicht in der Provision enthalten',
            '**Verpackung** inkl. Besteck, Servietten, Tüte (0,60 – 1,50 € je Bestellung)',
            '**Zusätzliche Personalzeit** für Packen und Übergabe',
            '**Werbekosten** auf der Plattform (Platzierungen, Rabattaktionen)',
            '**Stornos und Reklamationen** (Gutschriften, Ersatzlieferungen)',
          ])
        ),
        section(
          'Rechenbeispiel: Eine Bestellung über 30 €',
          table(
            ['Position', 'Plattform (30 % Provision)', 'Eigener Shop'],
            [
              ['Bestellwert (netto, nach MwSt.)', '28,04 €', '28,04 €'],
              ['Wareneinsatz 30 %', '− 8,41 €', '− 8,41 €'],
              ['Provision / Zahlungsgebühr', '− 9,00 €', '− 0,60 €'],
              ['Verpackung', '− 1,00 €', '− 1,00 €'],
              ['Packzeit 4 Min. à 25 €/Std.', '− 1,67 €', '− 1,67 €'],
              ['**Deckungsbeitrag**', '**7,96 €**', '**16,36 €**'],
            ]
          ),
          p('Vereinfachte Darstellung mit 7 % Umsatzsteuer auf Speisen zum Mitnehmen; bei Getränken gilt der volle Satz. Der Unterschied zwischen den Kanälen ist deutlich – und er zeigt, warum die Verlagerung in den eigenen Kanal der größte Hebel ist.')
        ),
        section(
          'Stellschrauben für bessere Margen',
          steps([
            '**Eigene Lieferpreise:** 10 – 15 % Aufschlag auf Plattformen sind üblich und für Gäste akzeptiert.',
            '**Mindestbestellwert** anheben, damit Verpackung und Packzeit sich verteilen.',
            '**Bundles und Menüs** mit hohem Deckungsbeitrag prominent platzieren.',
            '**Getränke und Desserts** aktiv anbieten – hoher Deckungsbeitrag, geringer Aufwand.',
            '**Lieferkarte verkleinern:** Gerichte mit hohem Wareneinsatz oder schlechter Transportqualität streichen.',
            '**Verpackung optimieren:** Standardgrößen, Mengenrabatt beim Einkauf, keine Überverpackung.',
            '**Werbeaktionen prüfen:** Plattform-Rabatte nur, wenn der Deckungsbeitrag nach Rabatt positiv bleibt.',
          ])
        ),
        section(
          'Break-even und Entscheidung',
          p('Delivery lohnt sich, wenn der Deckungsbeitrag aller Lieferbestellungen die zusätzlich verursachten Fixkosten übersteigt – etwa zusätzliches Personal, Verpackungslager, Plattform-Grundgebühren. Nutzt du nur freie Kapazitäten von Küche und Personal, ist fast jeder positive Deckungsbeitrag ein Gewinn. Musst du eine zusätzliche Küchenkraft einplanen, brauchst du bei 8 € Deckungsbeitrag pro Bestellung rund 25 Bestellungen pro Schicht, nur um diese zu bezahlen.'),
          warn('Umsatzwachstum durch Delivery ist kein Erfolg, wenn die Marge sinkt. Bewerte das Liefergeschäft immer nach Deckungsbeitrag – und rechne den Preis für belastete Restaurantgäste (längere Wartezeiten, schlechtere Bewertungen) mit ein.')
        ),
      ],
    },
  ],
};
